// src/hooks/useBreakpoint.ts

import { useState, useLayoutEffect } from "react";
import { BREAKPOINTS, TailwindBreakpoint } from "@/constants/breakpoints";

/**
 * Custom hook to determine if the current viewport matches the specified Tailwind CSS breakpoint.
 * @param breakpoint - The Tailwind CSS breakpoint name (e.g., "sm", "md", "lg", "xl", "2xl").
 * @returns A boolean indicating whether the breakpoint matches.
 */
function useBreakpoint(breakpoint: TailwindBreakpoint): boolean {
  const mediaQuery = BREAKPOINTS[breakpoint];

  // Initialize state based on the current viewport
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia(mediaQuery).matches;
    }
    return false; // Default value for SSR
  });

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQueryList = window.matchMedia(mediaQuery);

    // Define a handler for media query changes.
    const handleChange = (event: { matches: boolean | ((_prevState: boolean) => boolean); }) => {
      setMatches(event.matches);
    };

    // Add the event listener for media query changes.
    mediaQueryList.addEventListener("change", handleChange);

    // Cleanup the event listener on unmount.
    return () => {
      mediaQueryList.removeEventListener("change", handleChange);
    };
  }, [mediaQuery]);

  return matches;
}

export default useBreakpoint;
